/* @flow */

import React, { Component } from 'react';
import glamorous from 'glamorous';
import ReactDOM from 'react-dom';
import { range, sum } from 'lodash';
import { TimelineMax, TweenMax, Power3 } from 'gsap';
import GroupCardsDesktopOpen from 'app/components/friends/groupCardCarousel/GroupCardsDesktopOpen';
import GroupCardsDesktopClosed from 'app/components/friends/groupCardCarousel/GroupCardsDesktopClosed';

type Props = {
  currentGroupId?: number | string,
  groups?: Array<FriendGroup>,
  onCardClick: (number | string) => void,
  onOpenChangeDone: (boolean) => void,
  open: boolean,
};

type State = {
  animating: boolean,
  currentGroupId: string | number,
  isOpen: boolean,
};

const CarouselContainer = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  marginBottom: 20,
  position: 'relative',
});

class GroupCardsDesktop extends Component<Props, State> {
  props: Props;
  closedCarouselRef: HTMLElement; // eslint-disable-line react/sort-comp
  containerRef: HTMLElement;
  openCarouselRef: HTMLElement;

  constructor(props: Props) {
    super(props);
    this.state = {
      animating: false,
      currentGroupId: props.currentGroupId,
      isOpen: props.open,
    };
  }

  static defaultProps = {
    currentGroupId: undefined,
    groups: [],
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.open !== this.props.open) {
      this.setState({
        animating: true,
        // if we're closing it, don't update the group id
        // we need to stick around to render the leaving one correctly
        currentGroupId: nextProps.open ? nextProps.currentGroupId : this.state.currentGroupId,
      });
    } else if (!this.state.animating) {
      this.setState({ currentGroupId: nextProps.currentGroupId });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const isOpening = !prevProps.open && this.props.open;
    const isClosing = prevProps.open && !this.props.open;

    if (isOpening) {
      this.animateOpen().then(() => {
        this.setState({ animating: false, isOpen: true }, () => {
          this.props.onOpenChangeDone(true);
        });
      });
    }

    if (isClosing) {
      this.animateClose().then(() => {
        this.setState({ animating: false, isOpen: false }, () => {
          this.props.onOpenChangeDone(false);
        });
      });
    }
  }

  setOpenRef = (node: HTMLElement) => {
    this.openCarouselRef = node;
  };

  setClosedRef = (node: HTMLElement) => {
    this.closedCarouselRef = node;
  };

  setContainerRef = (node: HTMLElement) => {
    this.containerRef = node;
  };

  animateOpen = () => new Promise((resolve) => {
    const { groups } = this.props;
    const { currentGroupId } = this.state;
    // we only show 5 a page, so selected group will be at modded by 5
    const currentIndex = groups.findIndex(g => g.id === currentGroupId) % 5;

    // get all our components we'll use
    const openCarousel = ReactDOM.findDOMNode(this.openCarouselRef);
    const closedCarousel = ReactDOM.findDOMNode(this.closedCarouselRef);
    const mainGroupRef = this.containerRef.parentNode.parentNode;

    // place open carousel both hidden and where the closed one will end up
    // i.e. the very center
    TweenMax.set(openCarousel, {
      display: 'none',
      height: 110,
      width: '27%',
      left: '50%',
      x: '-50%',
      position: 'absolute',
      flexGrow: 0,
    });

    TweenMax.set(this.containerRef, { position: 'absolute', top: 0, bottom: 0 });
    const { height } = window.getComputedStyle(this.containerRef);
    TweenMax.set(this.containerRef, { clearProps: 'position,top,bottom' });

    /**
     * Timeline:
     * Move cards into center - 0.5s
     * fade the open carousel in on top - 0.2s
     * expand the open carousel - 0.5s
     * resolve it here, this causes it to fade (at 1.2s)
     * fade out the overlay - 0.2s
     * total time: 1.4s
     */
    const timeline = new TimelineMax();
    // move the cards in together
    const closedChildren = [...closedCarousel.querySelectorAll('[class*="card"]')];

    closedChildren.forEach((child, index) => {
      const indexDiff = index - currentIndex;
      timeline.add(() => TweenMax.fromTo(child, 0.5,
        { zIndex: 3 - Math.abs(indexDiff), x: '0%' },
        {
          ...this.getNextX(index, currentIndex),
          ease: Power3.easeInOut,
        },
      ));
    });

    timeline.add(() => {
      const openChildTitlePusher = openCarousel.querySelector('.pusher');
      // move the title from right to middle
      TweenMax.fromTo(openChildTitlePusher, 0.2,
        { flexBasis: '100%' },
        { flexBasis: '0%' });
      // fade in our carousel on top
      TweenMax.fromTo(openCarousel, 0.2,
      { display: '', opacity: 0, zIndex: 10 },
      { opacity: 1 });
    }, 0.5);

    timeline.add(() => {
      // set up things in prep for grow
      TweenMax.set(closedCarousel, { display: 'none' });
      TweenMax.set(openCarousel, { margin: '0 auto', position: 'relative', clearProps: 'x,left' });
      TweenMax.set(mainGroupRef, { maxHeight: `${parseInt(height, 10) + 20}px`, overflow: 'hidden' });
      // grow the carousel
      TweenMax.to(openCarousel, 0.5, { height, width: '100%', ease: Power3.easeOut, clearProps: 'all' });
    }, 0.7);

    // at this point the overlay will fade in
    timeline.add(() => {
      TweenMax.set(mainGroupRef, { clearProps: 'all' });
      TweenMax.set(openCarousel, { clearProps: 'all' });
      resolve();
    }, 1.2);
    timeline.play();
  });

  animateClose = () => new Promise((resolve) => {
    const {
      groups,
    } = this.props;
    const { currentGroupId } = this.state;
    const currentIndex = groups.findIndex(g => g.id === currentGroupId) % 5;

    // get all our components
    const openCarousel = ReactDOM.findDOMNode(this.openCarouselRef);
    const closedCarousel = ReactDOM.findDOMNode(this.closedCarouselRef);
    const mainGroupRef = this.containerRef.parentNode.parentNode;

    TweenMax.set(closedCarousel, { display: 'none' });

    const { height } = window.getComputedStyle(mainGroupRef);
    const openCarouselHeight = parseInt(height, 10) - 20;
    TweenMax.set(mainGroupRef, { maxHeight: height, overflow: 'hidden' });
    TweenMax.set(openCarousel, {
      height: openCarouselHeight,
      zIndex: 1,
      alignSelf: 'center',
      position: 'relative',
    });

    /**
     * Timeline:
     * Fade in overlay with title - 0.2s
     * Shrink the carousel (and open cards) - 0.5s
     * Fade out the open carousel - 0.2s
     * move the closed cards into position - 0.5s
     * total time: 1.4s
     */
    // first up, the overlay will fade in
    const timeline = new TimelineMax();
    // next we'll shrink the carousel
    timeline.add(() => TweenMax.fromTo(openCarousel, 0.5,
      { height: openCarouselHeight, width: '100%' },
      { height: 110, width: '27%', ease: Power3.easeInOut }),
    0.2);

    const closedChildren = [...closedCarousel.querySelectorAll('[class*="card"]')];
    closedChildren.forEach((child, index) => {
      // set up the card positions to they're in the center
      const nextX = this.getNextX(index, currentIndex);
      // then fan them out later
      timeline.add(TweenMax.fromTo(child, 0.5,
        nextX,
        { x: '0%', scale: 1, opacity: 1, ease: Power3.easeInOut, clearProps: 'all' }),
      0.9);
    });

    timeline.add(() => {
      // display the closed carousel
      TweenMax.set(closedCarousel, { display: '', zIndex: 0 });
      const openChildTitlePusher = openCarousel.querySelector('.pusher');
      // move the title from middle to right
      TweenMax.fromTo(openChildTitlePusher, 0.2,
        { flexBasis: '0%' },
        { flexBasis: '100%' });
      // fade out open that's on top of the closed one
      TweenMax.fromTo(openCarousel, 0.2, {
        opactiy: 1,
        position: 'absolute',
        top: 0,
        height: 110,
      }, { opacity: 0 });
    }, 0.7);

    timeline.add(() => {
      TweenMax.set(mainGroupRef, { clearProps: 'all' });
      TweenMax.set(closedCarousel, { clearProps: 'all' });
      resolve();
    }, 1.4);
    timeline.play();
  });

  getNextX = (index, selectedIndex) => {
    // find the selected items offset from the center
    // we need to move everything along by this much
    // 2 is center point number of 5, i.e. where we want it to go.
    // so work out how far way from this center point it is
    const offsetToCenter = (2 - selectedIndex) * 100;
    // negative is on left side, positive on right side of center
    const indexDiff = index - selectedIndex;
    // we need to both set it up so the one selected is the main visible
    // we also need to make sure it moves to the center
    const positiveDiff = Math.abs(indexDiff);
    const nextX = (100 * -indexDiff) + (10 * indexDiff) + offsetToCenter;
    const nextZ = Math.max(3 - positiveDiff, 0);
    const nextScale = 1 - (positiveDiff * 0.1);
    const nextOpacity = Math.max(1 - (sum(range(0, positiveDiff + 1)) * 0.2), 0);

    return { x: `${nextX}%`, zIndex: nextZ, opacity: nextOpacity, scale: nextScale };
  }

  render() {
    const {
      groups,
      onCardClick,
    } = this.props;
    const {
      animating,
      currentGroupId,
      isOpen,
    } = this.state;

    return (
      <CarouselContainer innerRef={this.setContainerRef}>
        {(isOpen || animating) && (
          <GroupCardsDesktopOpen
            key="desktopopen"
            groups={groups}
            currentGroupId={currentGroupId}
            onCardClick={onCardClick}
            ref={this.setOpenRef}
            animating={animating}
          />
        )}
        {(!isOpen || animating) && (
          <GroupCardsDesktopClosed
            key="desktopclosed"
            groups={groups}
            currentGroupId={currentGroupId}
            onCardClick={onCardClick}
            ref={this.setClosedRef}
          />
        )}
      </CarouselContainer>
    );
  }
}

export default GroupCardsDesktop;
